import React from 'react';
import { Badge } from '@/components/ui/badge';
import {
  LayoutDashboard, FileText, KanbanSquare, Boxes, BarChart3, Wallet,
} from 'lucide-react';

/* ---------------- Brand mark ---------------- */
export const LogoMark = ({ className = 'h-9 w-9' }) => (
  <div className={`flex items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm ${className}`}>
    <svg viewBox="0 0 24 24" className="h-1/2 w-1/2" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 20 L12 4 L20 20" />
      <path d="M7.5 13.5 L16.5 13.5" />
    </svg>
  </div>
);

/* ---------------- Browser frame ---------------- */
export const BrowserMockup = ({ title = 'app.aluminaone.com', children, testid }) => (
  <div data-testid={testid} className="overflow-hidden rounded-2xl border border-border bg-card shadow-[var(--shadow-lg)]">
    <div className="flex items-center gap-1.5 border-b border-border bg-muted/50 px-4 py-2.5">
      <span className="h-2.5 w-2.5 rounded-full bg-destructive/50" />
      <span className="h-2.5 w-2.5 rounded-full bg-warning/60" />
      <span className="h-2.5 w-2.5 rounded-full bg-success/60" />
      <div className="ml-3 flex-1 truncate rounded-md bg-background/70 px-2 py-1 text-[10px] text-muted-foreground">{title}</div>
      <Badge variant="outline" className="hidden rounded-full border-primary/30 bg-primary/5 text-[9px] text-primary sm:inline-flex">Demo</Badge>
    </div>
    {children}
  </div>
);

const MiniNav = ({ active }) => {
  const items = [
    ['Beranda', LayoutDashboard], ['Penawaran', FileText], ['Proyek', KanbanSquare],
    ['Stok', Boxes], ['Pembayaran', Wallet], ['Laporan', BarChart3],
  ];
  return (
    <div className="hidden w-40 shrink-0 border-r border-border bg-muted/30 p-3 sm:block">
      <div className="flex items-center gap-1.5 px-1 pb-3">
        <LogoMark className="h-5 w-5 rounded-md" />
        <span className="font-display text-[11px] font-semibold">AluminaOne</span>
      </div>
      <div className="space-y-0.5">
        {items.map(([label, Icon]) => (
          <div key={label} className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-[10px] ${active === label ? 'bg-primary/10 font-semibold text-primary' : 'text-muted-foreground'}`}>
            <Icon className="h-3 w-3" /> {label}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------------- Dashboard ---------------- */
export const DashboardMockup = () => {
  const kpis = [['Omzet bulan ini', 'Rp 42,8 jt', '+12%'], ['Proyek aktif', '14', '+3'], ['Piutang', 'Rp 9,4 jt', '-8%']];
  const bars = [42, 64, 50, 88, 70, 96, 60];
  const rows = [
    ['Kanopi Ruko — Andi', 'Produksi', 'bg-primary/10 text-primary'],
    ['Partisi Kantor — PT Maju', 'Pemasangan', 'bg-info/10 text-info'],
    ['Pintu Kaca — Bu Sinta', 'Selesai', 'bg-success/10 text-success'],
  ];
  return (
    <BrowserMockup title="app.aluminaone.com · Beranda" testid="mockup-dashboard-overview">
      <div className="flex">
        <MiniNav active="Beranda" />
        <div className="min-w-0 flex-1 space-y-3 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] text-muted-foreground">Selamat datang,</div>
              <div className="font-display text-sm font-semibold">Workshop Jaya Makmur</div>
            </div>
            <Badge className="bg-primary/10 text-[9px] text-primary">Paket Pro</Badge>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {kpis.map(([l, v, d]) => (
              <div key={l} className="rounded-xl border border-border bg-background p-2.5">
                <div className="text-[9px] text-muted-foreground">{l}</div>
                <div className="mt-1 font-display text-xs font-bold tabular-nums">{v}</div>
                <div className="text-[9px] font-medium text-success">{d}</div>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-border bg-background p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[10px] font-medium">Omzet 7 hari terakhir</span>
              <span className="text-[9px] text-muted-foreground">Rp (juta)</span>
            </div>
            <div className="flex h-16 items-end gap-1.5">
              {bars.map((h, i) => (
                <div key={i} className={`flex-1 rounded-t ${i === 5 ? 'bg-primary' : 'bg-primary/30'}`} style={{ height: `${h}%` }} />
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-border bg-background p-3">
            <div className="mb-2 text-[10px] font-medium">Proyek berjalan</div>
            <div className="space-y-1.5">
              {rows.map(([name, status, cls]) => (
                <div key={name} className="flex items-center justify-between">
                  <span className="truncate text-[10px] text-foreground">{name}</span>
                  <span className={`ml-2 shrink-0 rounded-full px-2 py-0.5 text-[8px] font-semibold ${cls}`}>{status}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </BrowserMockup>
  );
};

/* ---------------- Quotation ---------------- */
export const QuotationMockup = () => {
  const items = [
    ['Jendela Aluminium 1,2 × 1,5 m', '4', 'Rp 1.250.000', 'Rp 5.000.000'],
    ['Pintu Kaca Tempered 12mm', '2', 'Rp 2.150.000', 'Rp 4.300.000'],
    ['Kusen Aluminium Powder Coat', '6', 'Rp 380.000', 'Rp 2.280.000'],
  ];
  return (
    <BrowserMockup title="app.aluminaone.com · Penawaran" testid="mockup-quotation-builder">
      <div className="flex">
        <MiniNav active="Penawaran" />
        <div className="min-w-0 flex-1 space-y-3 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display text-sm font-semibold">Penawaran #PN-0241</div>
              <div className="text-[10px] text-muted-foreground">Pelanggan: Bpk. Andi · Bekasi</div>
            </div>
            <Badge className="bg-warning/15 text-[9px] text-warning-foreground">Draft</Badge>
          </div>
          <div className="overflow-hidden rounded-xl border border-border">
            <div className="grid grid-cols-12 gap-1 border-b border-border bg-muted/50 px-3 py-1.5 text-[9px] font-semibold text-muted-foreground">
              <span className="col-span-6">Item</span><span className="col-span-1 text-right">Qty</span><span className="col-span-2 text-right">Harga</span><span className="col-span-3 text-right">Subtotal</span>
            </div>
            {items.map(([n, q, h, s]) => (
              <div key={n} className="grid grid-cols-12 gap-1 border-b border-border/60 px-3 py-1.5 text-[9px]">
                <span className="col-span-6 truncate">{n}</span>
                <span className="col-span-1 text-right tabular-nums text-muted-foreground">{q}</span>
                <span className="col-span-2 text-right tabular-nums text-muted-foreground">{h}</span>
                <span className="col-span-3 text-right font-medium tabular-nums">{s}</span>
              </div>
            ))}
            <div className="flex items-center justify-between bg-primary/5 px-3 py-2">
              <span className="text-[10px] font-semibold">Total</span>
              <span className="font-display text-xs font-bold tabular-nums text-primary">Rp 11.580.000</span>
            </div>
          </div>
          <div className="flex gap-2">
            <div className="flex-1 rounded-lg bg-primary px-3 py-1.5 text-center text-[9px] font-semibold text-primary-foreground">Kirim PDF</div>
            <div className="flex-1 rounded-lg border border-border px-3 py-1.5 text-center text-[9px] font-medium text-muted-foreground">Bagikan WhatsApp</div>
          </div>
        </div>
      </div>
    </BrowserMockup>
  );
};

/* ---------------- Kanban ---------------- */
export const KanbanMockup = () => {
  const cols = [
    ['Ukur', ['Kanopi — Andi', 'Railing — Dewi']],
    ['Produksi', ['Partisi — PT Maju', 'Jendela — Sari']],
    ['Pasang', ['Pintu — Bu Sinta']],
    ['Selesai', ['Folding — Toko Jaya']],
  ];
  const tone = ['border-l-muted-foreground/40', 'border-l-primary', 'border-l-info', 'border-l-success'];
  return (
    <BrowserMockup title="app.aluminaone.com · Proyek" testid="mockup-project-kanban">
      <div className="flex">
        <MiniNav active="Proyek" />
        <div className="min-w-0 flex-1 p-4">
          <div className="mb-3 font-display text-sm font-semibold">Papan Proyek</div>
          <div className="grid grid-cols-4 gap-2">
            {cols.map(([title, cards], ci) => (
              <div key={title} className="rounded-xl border border-border bg-muted/30 p-2">
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-[9px] font-semibold text-muted-foreground">{title}</span>
                  <span className="rounded-full bg-background px-1.5 text-[8px] text-muted-foreground">{cards.length}</span>
                </div>
                <div className="space-y-1.5">
                  {cards.map((c) => (
                    <div key={c} className={`rounded-lg border border-border border-l-2 bg-background p-1.5 text-[9px] ${tone[ci]}`}>
                      <div className="truncate font-medium">{c}</div>
                      <div className="mt-0.5 text-[8px] text-muted-foreground">Jatuh tempo 3 hari</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </BrowserMockup>
  );
};

/* ---------------- Reports ---------------- */
export const ReportsMockup = () => {
  const rows = [
    ['Kanopi Ruko Andi', 'Rp 3,2 jt', '38%'],
    ['Partisi PT Maju', 'Rp 5,8 jt', '31%'],
    ['Pintu Kaca Bu Sinta', 'Rp 1,9 jt', '44%'],
  ];
  const pts = '0,40 30,32 60,36 90,22 120,26 150,14 180,18 210,8';
  return (
    <BrowserMockup title="app.aluminaone.com · Laporan" testid="mockup-reports">
      <div className="flex">
        <MiniNav active="Laporan" />
        <div className="min-w-0 flex-1 space-y-3 p-4">
          <div className="font-display text-sm font-semibold">Laporan Profit</div>
          <div className="rounded-xl border border-border bg-background p-3">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-[10px] font-medium">Tren profit (6 bln)</span>
              <span className="text-[9px] font-semibold text-success">+18%</span>
            </div>
            <svg viewBox="0 0 210 48" className="h-16 w-full" preserveAspectRatio="none">
              <polyline points={pts} fill="none" stroke="hsl(var(--primary))" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="rounded-xl border border-border bg-background p-3">
            <div className="mb-2 text-[10px] font-medium">Profit per proyek</div>
            <div className="space-y-1.5">
              {rows.map(([n, v, m]) => (
                <div key={n} className="flex items-center justify-between text-[9px]">
                  <span className="truncate text-foreground">{n}</span>
                  <span className="flex items-center gap-2">
                    <span className="font-medium tabular-nums">{v}</span>
                    <span className="rounded-full bg-success/10 px-1.5 py-0.5 text-[8px] font-semibold text-success">{m}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </BrowserMockup>
  );
};
